import React, { useState, useRef, useEffect } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../hooks/useAuth';
import { formatDate, formatCurrency } from '../../utils/helpers';
import { NotificationType, Notification, Role } from '../../types';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Bell,
  X,
  MessageCircle,
  Info,
  CheckCircle,
  XCircle,
  Package,
  UserPlus,
  Headphones,
  Ticket,
  AlertTriangle,
  Wallet,
  PackageSearch,
  AlertOctagon,
  RotateCcw,
  type LucideIcon,
} from 'lucide-react';

const { useNavigate } = ReactRouterDOM as any;

/** Couleurs de badge par catégorie : bleu commandes, violet tickets, orange dépôts, vert admin, rouge alertes */
const NOTIFICATION_BADGE_COLORS = {
  order: 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400',
  ticket: 'bg-accent-100 dark:bg-accent-900/40 text-accent-600 dark:text-accent-400',
  deposit: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400',
  admin: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
  alert: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
  default: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
} as const;

type NotificationCategory = keyof typeof NOTIFICATION_BADGE_COLORS;

interface NotificationTypeConfig {
  Icon: LucideIcon;
  badgeColor: NotificationCategory;
}

const NOTIFICATION_TYPE_CONFIG: Record<NotificationType, NotificationTypeConfig> = {
  // Tickets → violet
  [NotificationType.TICKET_REPONSE]: { Icon: MessageCircle, badgeColor: 'ticket' },
  [NotificationType.TICKET_STATUT]: { Icon: Info, badgeColor: 'ticket' },
  [NotificationType.TICKET_CREE]: { Icon: Ticket, badgeColor: 'ticket' },
  [NotificationType.TICKET_URGENCE]: { Icon: AlertTriangle, badgeColor: 'ticket' },
  // Dépôts → orange
  [NotificationType.DEPOT_VALIDE]: { Icon: CheckCircle, badgeColor: 'deposit' },
  [NotificationType.DEPOT_REFUSE]: { Icon: XCircle, badgeColor: 'deposit' },
  [NotificationType.DEPOT_CREE]: { Icon: Wallet, badgeColor: 'deposit' },
  // Commandes → bleu
  [NotificationType.COMMANDE_STATUT]: { Icon: Package, badgeColor: 'order' },
  [NotificationType.COMMANDE_CREEE]: { Icon: Package, badgeColor: 'order' },
  [NotificationType.COMMANDE_PAYEE]: { Icon: Package, badgeColor: 'order' },
  [NotificationType.COMMANDE_LIVREE]: { Icon: Package, badgeColor: 'order' },
  [NotificationType.COMMANDE_ANNULEE]: { Icon: Package, badgeColor: 'order' },
  [NotificationType.COMMANDE_LITIGE]: { Icon: Package, badgeColor: 'order' },
  // Admin / nouveaux clients → vert
  [NotificationType.NOUVEAU_CLIENT]: { Icon: UserPlus, badgeColor: 'admin' },
  [NotificationType.NOUVEAU_TICKET]: { Icon: Headphones, badgeColor: 'ticket' },
  // Alertes système → rouge
  [NotificationType.STOCK_FAIBLE]: { Icon: PackageSearch, badgeColor: 'alert' },
  [NotificationType.STOCK_EPUISE]: { Icon: PackageSearch, badgeColor: 'alert' },
  [NotificationType.SYSTEM_ALERT]: { Icon: AlertOctagon, badgeColor: 'alert' },
};

const DEFAULT_CONFIG: NotificationTypeConfig = { Icon: Info, badgeColor: 'default' };

/** Catégories filtrables (sans 'default') */
const FILTER_CATEGORIES = ['order', 'ticket', 'deposit', 'admin', 'alert'] as const;
type FilterCategory = typeof FILTER_CATEGORIES[number];

function getCategoryForType(type: NotificationType): NotificationCategory {
  return (NOTIFICATION_TYPE_CONFIG[type] ?? DEFAULT_CONFIG).badgeColor;
}

function NotificationIconBadge({ type }: { type: NotificationType }) {
  const config = NOTIFICATION_TYPE_CONFIG[type] ?? DEFAULT_CONFIG;
  const { Icon, badgeColor } = config;
  const colorClass = NOTIFICATION_BADGE_COLORS[badgeColor];
  return (
    <span
      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${colorClass}`}
      aria-hidden
    >
      <Icon className="h-4 w-4" strokeWidth={2} />
    </span>
  );
}


/**
 * Normalise le lien d'une notification selon son type et le rôle de l'utilisateur
 */
const normalizeNotificationLink = (notification: Notification, isAdmin: boolean): string => {
  const { type, lien, metadata } = notification;
  const basePath = isAdmin ? '/admin' : '/dashboard';
  
  // Extraire les IDs depuis le lien (gère à la fois / et #)
  const extractIdFromPath = (path: string, pattern: string): string | null => {
    // Chercher avec / (ex: /orders/123)
    let match = path.match(new RegExp(`${pattern}/(\\d+)`));
    if (match) return match[1];
    // Chercher avec # (ex: /orders#123)
    match = path.match(new RegExp(`${pattern}#(\\d+)`));
    if (match) return match[1];
    return null;
  };

  // Si le lien existe déjà et commence par /admin ou /dashboard
  if (lien && (lien.startsWith('/admin') || lien.startsWith('/dashboard'))) {
    // Pour les admins, s'assurer que les liens commencent par /admin
    if (isAdmin && lien.startsWith('/dashboard')) {
      // Convertir les liens client en liens admin
      if (lien.includes('/deposits')) {
        const depositId = extractIdFromPath(lien, '/deposits') || metadata?.deposit_id;
        return depositId ? `/admin/deposits#${depositId}` : '/admin/deposits';
      }
      if (lien.includes('/orders')) {
        const orderId = extractIdFromPath(lien, '/orders') || metadata?.order_id;
        return orderId ? `/admin/orders#${orderId}` : '/admin/orders';
      }
      if (lien.includes('/support')) {
        const ticketId = extractIdFromPath(lien, '/support') || metadata?.ticket_id;
        return ticketId ? `/admin/support/${ticketId}` : '/admin/support';
      }
      if (lien.includes('/users') || lien.includes('/clients')) {
        // Privilégier metadata.user_id (UUID) si disponible
        if (metadata?.user_id) {
          const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          if (uuidPattern.test(metadata.user_id)) {
            return `/admin/users/${metadata.user_id}`;
          }
        }
        const userId = extractIdFromPath(lien, '/users') || extractIdFromPath(lien, '/clients');
        // Vérifier que l'ID extrait est un UUID valide, sinon utiliser metadata
        if (userId) {
          const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          if (uuidPattern.test(userId)) {
            return `/admin/users/${userId}`;
          } else if (metadata?.user_id) {
            // Si le lien contient un nombre au lieu d'un UUID, utiliser metadata
            return `/admin/users/${metadata.user_id}`;
          }
        }
        return '/admin/users';
      }
    }
    // Pour les clients, s'assurer que les liens commencent par /dashboard
    if (!isAdmin && lien.startsWith('/admin')) {
      // Convertir les liens admin en liens client
      if (lien.includes('/deposits')) {
        const depositId = extractIdFromPath(lien, '/deposits') || metadata?.deposit_id;
        return depositId ? `/dashboard/deposits#${depositId}` : '/dashboard/deposits';
      }
      if (lien.includes('/orders')) {
        const orderId = extractIdFromPath(lien, '/orders') || metadata?.order_id;
        return orderId ? `/dashboard/orders#${orderId}` : '/dashboard/orders';
      }
      if (lien.includes('/support')) {
        const ticketId = extractIdFromPath(lien, '/support') || metadata?.ticket_id;
        return ticketId ? `/dashboard/support/${ticketId}` : '/dashboard/support';
      }
    }
    // Pour les admins, corriger les liens avec / au lieu de # pour orders et deposits
    if (isAdmin && lien.startsWith('/admin')) {
      if (lien.includes('/orders/')) {
        const orderId = extractIdFromPath(lien, '/orders') || metadata?.order_id;
        return orderId ? `/admin/orders#${orderId}` : '/admin/orders';
      }
      if (lien.includes('/deposits/')) {
        const depositId = extractIdFromPath(lien, '/deposits') || metadata?.deposit_id;
        return depositId ? `/admin/deposits#${depositId}` : '/admin/deposits';
      }
      // Corriger /admin/clients/ en /admin/users/
      if (lien.includes('/clients/')) {
        const userId = extractIdFromPath(lien, '/clients') || metadata?.user_id;
        return userId ? `/admin/users/${userId}` : '/admin/users';
      }
    }
    // Si le lien est déjà correct pour le rôle, le retourner tel quel
    return lien;
  }

  // Construire le lien selon le type de notification et le rôle
  switch (type) {
    case NotificationType.DEPOT_VALIDE:
    case NotificationType.DEPOT_REFUSE:
    case NotificationType.DEPOT_CREE:
      return `${basePath}/deposits`;

    case NotificationType.COMMANDE_STATUT:
    case NotificationType.COMMANDE_CREEE:
    case NotificationType.COMMANDE_PAYEE:
    case NotificationType.COMMANDE_LIVREE:
    case NotificationType.COMMANDE_ANNULEE:
      // Si on a un order_id dans les métadonnées, on peut l'ajouter comme ancre
      if (metadata?.order_id) {
        return `${basePath}/orders#${metadata.order_id}`;
      }
      return `${basePath}/orders`;

    case NotificationType.NOUVEAU_CLIENT:
      if (isAdmin) {
        // Pour les admins, TOUJOURS utiliser metadata.user_id (UUID) si disponible
        // Ignorer le lien s'il contient un nombre au lieu d'un UUID
        if (metadata?.user_id) {
          // Vérifier que c'est bien un UUID (format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
          const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          if (uuidPattern.test(metadata.user_id)) {
            return `/admin/users/${metadata.user_id}`;
          }
        }
        // Si le lien existe et contient un UUID, l'utiliser
        if (lien) {
          const extractedId = extractIdFromPath(lien, '/users') || extractIdFromPath(lien, '/clients');
          if (extractedId) {
            const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            if (uuidPattern.test(extractedId)) {
              return `/admin/users/${extractedId}`;
            }
          }
        }
        return '/admin/users';
      }
      // Pour les clients, rediriger vers le dashboard
      return basePath;

    case NotificationType.NOUVEAU_TICKET:
    case NotificationType.TICKET_REPONSE:
    case NotificationType.TICKET_STATUT:
      if (metadata?.ticket_id) {
        return `${basePath}/support/${metadata.ticket_id}`;
      }
      return `${basePath}/support`;

    default:
      // Si aucun cas ne correspond, retourner le lien original ou le dashboard
      return lien || basePath;
  }
};

/**
 * Retourne le message de notification traduit selon le type et les metadata (i18n).
 */
const getNotificationDisplayMessage = (
  notif: Notification,
  t: TFunction,
  formatCurrencyFn: (amount: number) => string,
  isAdmin: boolean
): string => {
  const meta = notif.metadata || {};
  switch (notif.type) {
    case NotificationType.COMMANDE_STATUT:
    case NotificationType.COMMANDE_ANNULEE:
    case NotificationType.COMMANDE_LITIGE:
      if (meta.order_id != null && meta.order_status != null) {
        const statusLabel = t(`status.${meta.order_status}`, { defaultValue: meta.order_status });
        return t('notifications.order_status_changed', { orderId: meta.order_id, status: statusLabel });
      }
      break;
    case NotificationType.COMMANDE_LIVREE:
      if (meta.replacement_order_id != null && meta.original_order_id != null) {
        return t('notifications.replacement_order_created', {
          replacementId: meta.replacement_order_id,
          originalId: meta.original_order_id,
        });
      }
      break;
    case NotificationType.DEPOT_VALIDE:
      if (meta.amount != null && !Number.isNaN(Number(meta.amount))) {
        return t('notifications.deposit_validated', { amount: formatCurrencyFn(Number(meta.amount)) });
      }
      break;
    case NotificationType.DEPOT_REFUSE:
      if (meta.amount != null && !Number.isNaN(Number(meta.amount))) {
        return t('notifications.deposit_refused', { amount: formatCurrencyFn(Number(meta.amount)) });
      }
      break;
    case NotificationType.TICKET_REPONSE:
      if (meta.ticket_id != null) {
        return t('notifications.ticket_reply', { ticketId: meta.ticket_id });
      }
      break;
    case NotificationType.NOUVEAU_TICKET:
      if (isAdmin && meta.ticket_id != null) {
        return t('notifications.admin.new_ticket', { sujet: meta.sujet ?? `#${meta.ticket_id}` });
      }
      break;
    case NotificationType.NOUVEAU_CLIENT:
      if (isAdmin && (meta.prénom != null || meta.nom != null)) {
        const name = [meta.prénom, meta.nom].filter(Boolean).join(' ') || t('notifications.admin.new_client_unknown');
        return t('notifications.admin.new_client', { name });
      }
      break;
    default:
      break;
  }
  if (isAdmin && notif.type === NotificationType.COMMANDE_STATUT && meta.order_id != null && meta.amount != null && !Number.isNaN(Number(meta.amount))) {
    return t('notifications.admin.new_order', { orderId: meta.order_id, amount: formatCurrencyFn(Number(meta.amount)) });
  }
  if (isAdmin && meta.amount != null && meta.method != null && !Number.isNaN(Number(meta.amount))) {
    return t('notifications.admin.new_deposit', { amount: formatCurrencyFn(Number(meta.amount)), method: t(`status.${meta.method}`, { defaultValue: meta.method }) });
  }
  // Fallback traduit par type : ne jamais afficher le message brut du backend (souvent dans une seule langue)
  const genericKey = `notifications.generic.${notif.type}`;
  const genericMessage = t(genericKey, { defaultValue: '' });
  if (genericMessage) return genericMessage;
  return t('notifications.generic.default');
};

const Notifications: React.FC<{ triggerClassName?: string }> = ({ triggerClassName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<FilterCategory | null>(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const isAdmin = user?.role === Role.ADMIN;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    setIsOpen(false);
    const normalizedLink = normalizeNotificationLink(notification, isAdmin);
    navigate(normalizedLink);
  };

  const handleDeleteNotification = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    deleteNotification(notificationId);
  };

  const filteredNotifications =
    typeFilter === null
      ? notifications
      : notifications.filter((n) => getCategoryForType(n.type) === typeFilter);

  const filterButtonClass = (active: boolean) =>
    `flex flex-shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-surface ${
      active
        ? 'bg-primary-500/15 text-primary-700 ring-1 ring-primary-500/20 dark:bg-primary-500/20 dark:text-primary-200'
        : 'bg-surface text-foreground-muted hover:bg-primary-500/10 dark:bg-slate-800/80 dark:hover:bg-white/10'
    }`;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={
          triggerClassName ??
          'ui-focus-visible relative rounded-xl p-2 text-foreground-muted transition-colors hover:bg-primary-500/12 hover:text-primary-600 dark:hover:text-primary-300'
        }
        aria-label={t('common.notifications')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Bell className="h-6 w-6" aria-hidden />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-surface-card bg-gradient-to-br from-accent-500 to-accent-600 px-1 text-[10px] font-bold text-white shadow-sm dark:border-slate-900">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-80 max-h-96 origin-top-right overflow-y-auto rounded-xl border border-border bg-surface-card shadow-premium ltr:right-0 rtl:left-0 rtl:origin-top-left">
          <div className="sticky top-0 z-10 border-b border-border bg-surface-card">
            <div className="h-0.5 bg-gradient-brand" aria-hidden />
            <div className="flex items-center justify-between p-2">
              <h3 className="text-sm font-semibold text-foreground">{t('common.notifications')}</h3>
              {unreadCount > 0 && (
                <button type="button" onClick={markAllAsRead} className="text-xs text-primary-600 dark:text-primary-400 hover:underline focus:outline-none">
                  {t('common.read_all')}
                </button>
              )}
            </div>
            <div className="px-2 pb-2 flex flex-wrap gap-1.5" role="group" aria-label={t('notifications.filter_by_type', { defaultValue: 'Filtrer par type' })}>
              <button
                type="button"
                onClick={() => setTypeFilter(null)}
                className={filterButtonClass(typeFilter === null)}
                title={t('notifications.filter_all', { defaultValue: 'Toutes' })}
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                <span>{t('notifications.filter_all', { defaultValue: 'Toutes' })}</span>
              </button>
              <button
                type="button"
                onClick={() => setTypeFilter('order')}
                className={filterButtonClass(typeFilter === 'order')}
                title={t('notifications.filter_orders', { defaultValue: 'Commandes' })}
              >
                <Package className="h-3.5 w-3.5" aria-hidden />
                <span>{t('notifications.filter_orders', { defaultValue: 'Commandes' })}</span>
              </button>
              <button
                type="button"
                onClick={() => setTypeFilter('ticket')}
                className={filterButtonClass(typeFilter === 'ticket')}
                title={t('notifications.filter_tickets', { defaultValue: 'Tickets' })}
              >
                <Headphones className="h-3.5 w-3.5" aria-hidden />
                <span>{t('notifications.filter_tickets', { defaultValue: 'Tickets' })}</span>
              </button>
              <button
                type="button"
                onClick={() => setTypeFilter('deposit')}
                className={filterButtonClass(typeFilter === 'deposit')}
                title={t('notifications.filter_deposits', { defaultValue: 'Dépôts' })}
              >
                <Wallet className="h-3.5 w-3.5" aria-hidden />
                <span>{t('notifications.filter_deposits', { defaultValue: 'Dépôts' })}</span>
              </button>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setTypeFilter('admin')}
                  className={filterButtonClass(typeFilter === 'admin')}
                  title={t('notifications.filter_admin', { defaultValue: 'Clients' })}
                >
                  <UserPlus className="h-3.5 w-3.5" aria-hidden />
                  <span>{t('notifications.filter_admin', { defaultValue: 'Clients' })}</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setTypeFilter('alert')}
                className={filterButtonClass(typeFilter === 'alert')}
                title={t('notifications.filter_alerts', { defaultValue: 'Alertes' })}
              >
                <AlertOctagon className="h-3.5 w-3.5" aria-hidden />
                <span>{t('notifications.filter_alerts', { defaultValue: 'Alertes' })}</span>
              </button>
            </div>
          </div>
          <ul className="divide-y divide-border">
            {filteredNotifications.length > 0 ? (
              filteredNotifications.map((notif) => (
                <li
                  key={notif.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleNotificationClick(notif)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNotificationClick(notif); } }}
                  className={`flex cursor-pointer items-start gap-3 p-3 hover:bg-primary-500/[0.06] dark:hover:bg-white/[0.04] ${!notif.lu_a ? 'bg-primary-500/10 dark:bg-primary-500/15' : ''}`}
                >
                  <div className="flex-shrink-0 rtl:order-3">
                    <NotificationIconBadge type={notif.type} />
                  </div>
                  <div className="flex-grow min-w-0 rtl:order-1">
                    <p className="text-sm text-foreground">{getNotificationDisplayMessage(notif, t, formatCurrency, isAdmin)}</p>
                    <p className="mt-1 text-xs text-foreground-muted">{formatDate(notif.date ?? notif.created_at ?? '')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => handleDeleteNotification(e, notif.id)}
                    className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-red-500 rtl:order-2"
                    aria-label={t('common.delete', { defaultValue: 'Supprimer' })}
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                  {!notif.lu_a && <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0 mt-1.5 rtl:order-4" aria-hidden />}
                </li>
              ))
            ) : (
              <li className="p-4 text-center text-sm text-foreground-muted">
                {typeFilter !== null
                  ? t('notifications.empty_filtered', { defaultValue: 'Aucune notification dans cette catégorie.' })
                  : t('notifications.empty')}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Notifications;
